import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Select } from 'antd'
import {
  X,
  Ship,
  RefreshCw,
  Search,
  StopCircle,
  Play,
  History,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Plus,
} from 'lucide-react'
import { useEntityLink } from '../../../contexts/EntityLinkContext'
import { useContainerMonitor } from '../../../hooks/useContainerMonitor'
import { useExtensionEnrollment } from '../../../hooks/useExtensionEnrollment'
import {
  useExtensionLiveStatus,
  type ExtensionSessionInfo,
  type SessionRecheckResult,
  type SiteKeyInfo,
} from '../../../hooks/useExtensionLiveStatus'
import type {
  ContainerMonitor,
  ContainerStatus,
  LfdAlertLevel,
  PccRecord,
  StatusChangeLog,
} from '../../../types/scraper'
import { PCC_DATE_ORDER } from '../../../types/scraper'
import { HoldReasons } from '../../shared/HoldReasons'
import { holdAbbr, holdCategoryStyle, formatHolds } from '../../../utils/holdReasons'
import { formatLfdShort, lfdDateOnly } from '../../../utils/lfdDate'

interface ContainerMonitorModalProps {
  open: boolean
  onClose: () => void
}

// ==================== Status & Alert Helpers ====================

// 'unknown' is not a terminal-reported status — it's what we show when
// latest_status is NULL, i.e. no terminal has told us anything about this
// container yet. It used to fall back to 'On Vessel', which read as an
// observed fact when it was really just the seed value.
const UNKNOWN_STATUS = 'unknown'

// Same idea for the terminal column: no terminal_site means no terminal has
// claimed this container yet, which is exactly the set worth filtering to.
const UNKNOWN_TERMINAL = 'unknown'

// Not terminals — cross-cuts. PCC is the customs exam station, so a container
// filtered here still has whatever terminal it came off. The terminal column
// cannot answer either of these questions at all.
//
// Two of them, because they are two different piles and the dispatcher wants
// both:
//   EXAM_FILTER — everything CBP pulled for exam, the same set the board groups
//     under "Transfer for Exam · PCC Picked". Driven by the hold tag, so a box
//     shows up the moment the terminal says "exam", before PCC has logged it.
//   PCC_FILTER — of those, the ones PCC has on file. This is the set that got
//     pushed to CBP Hold.
// Collapsing them into one option is what made this read empty: three boxes
// were at exam all day and none had been confirmed yet.
const EXAM_FILTER = 'exam'
const PCC_FILTER = 'pcc'

// 任务一进 CBP Hold，柜子监控就按设计停掉了（码头已经答不上话，再问也是白问）。
// 停下来的这批依然是查验柜 —— 而且是最要紧的那几个 —— 所以这两个筛选项必须
// 认得这个停止原因，否则「Active」会把整批藏起来。
const CBP_HOLD_STOP_REASON = 'task_pcc_hold'

/**
 * 柜子在查验站 —— PCC 那边查到了这票货，或者任务已经被放进 CBP Hold
 * （调度手动推的那种，PCC 页面上还没有东西，但柜子确实被扣着）。
 */
const isAtPcc = (m: ContainerMonitor) => !!m.pcc_found_at || m.stop_reason === CBP_HOLD_STOP_REASON

/** 海关拉走的整堆：扣柜标签上带 exam 的，加上已经在查验站的。 */
const isExamPile = (m: ContainerMonitor) => m.latest_holds?.includes('exam') === true || isAtPcc(m)

const statusColors: Record<ContainerStatus | typeof UNKNOWN_STATUS, { bg: string; text: string; label: string }> = {
  'released': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Released' },
  'on-hold': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'On Hold' },
  'on-vessel': { bg: 'bg-slate-100', text: 'text-slate-600', label: 'On Vessel' },
  'picked-up': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Picked Up' },
  'unknown': { bg: 'bg-slate-100', text: 'text-slate-400', label: 'Unknown' },
}

/**
 * A blank status is not one fact, it is three — and "Unknown" next to a
 * Checked time from one minute ago was telling the dispatcher the wrong one.
 *
 * `latest_status` goes NULL whenever we have no reading for THIS voyage, and
 * three different things put it there. All three legitimately move
 * `last_checked_at`, because the check really did run (see handleNotFound and
 * handleResidueOnly on the backend — both say so, deliberately). So the row
 * read "checked 19:15 · Unknown", which the word Unknown defines as *nobody
 * has told us anything* — and in two of the three cases a terminal had just
 * told us something quite specific:
 *
 *   not_found_count > 0     the terminal answered and has no record of this
 *                           number at all. Normal for a box not yet
 *                           manifested; also what a wrong container number
 *                           looks like.
 *   residue_only_count > 0  the terminal answered with last trip's record
 *                           only. Evidence we are watching the WRONG
 *                           terminal — the backend re-opens the search after
 *                           two of these.
 *   both 0                  genuinely nothing: no answer has reached us.
 *
 * Why counters and not a new status value: `latest_status` drives task
 * transitions, and inventing a fourth value there would need the status
 * whitelist, the state machine and every consumer to agree. These two columns
 * already exist on the row, already ship to the client, and already mean
 * exactly this. The lie was only ever in the label.
 */
function describeBlankStatus(m: ContainerMonitor): { bg: string; text: string; label: string; title: string } {
  if ((m.residue_only_count || 0) > 0) {
    return {
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      label: 'Last trip only',
      title: 'The terminal answered, but all it has on file for this number is the previous voyage'
        + ' — this container is probably at a different terminal. The search re-opens automatically.',
    }
  }
  if ((m.not_found_count || 0) > 0) {
    return {
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      label: 'Not on file',
      title: 'The terminal answered and has no record of this container.'
        + ' Usual reasons: not discharged/manifested yet, or the container number is wrong.',
    }
  }
  return {
    ...statusColors[UNKNOWN_STATUS],
    title: 'No terminal has answered about this container yet — nothing has been read, so there is'
      + ' nothing to show. Not the same as the terminal saying it has no record.',
  }
}

/** Status badge styling + hover text for one row. */
function describeStatus(m: ContainerMonitor): { bg: string; text: string; label: string; title: string } {
  if (!m.latest_status) return describeBlankStatus(m)
  const style = statusColors[m.latest_status] || statusColors[UNKNOWN_STATUS]
  return { ...style, title: '' }
}

const alertColors: Record<LfdAlertLevel, { bg: string; text: string; dot: string }> = {
  'normal': { bg: '', text: 'text-slate-600', dot: 'bg-emerald-500' },
  'warning': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  'critical': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  'overdue': { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-700' },
}

const terminalLabels: Record<string, string> = {
  'oict': 'SSA',
  'ets': 'ETS',
  'trapac': 'TraPac',
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  const hhmm = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  // If not today, prepend MM/DD so stale checks can't masquerade as fresh.
  const now = new Date()
  const sameDay = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
  if (sameDay) return hhmm
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${hhmm}`
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}`
}

// 扣柜原因那几个标签搬去了 components/shared/HoldReasons —— 派单/柜子管理列表的
// On Hold 行要显示同一句话。这里把它再转出去一次，老的引用路径（含那份测试）不变。
export { HoldReasons }

// ==================== Sorting ====================

type SortKey = 'status' | 'holds' | 'client' | 'terminal' | 'vessel' | 'checked'
type SortDir = 'asc' | 'desc'

/**
 * Status is a category, not a word, so it sorts by how much attention the row
 * needs rather than alphabetically — "On Hold" before "Released" is the order
 * a dispatcher actually reads the list in.
 */
const statusSortRank: Record<string, number> = {
  'on-hold': 0,
  'released': 1,
  'on-vessel': 2,
  'picked-up': 3,
  [UNKNOWN_STATUS]: 4,
}

/**
 * The sort value for one row. Returning `null` means "no value here" — those
 * rows always sink to the bottom, in both directions, so flipping the arrow
 * never fills the top of the screen with blanks.
 */
function sortValue(m: ContainerMonitor, key: SortKey): string | number | null {
  switch (key) {
    case 'status': {
      const status = m.latest_status || UNKNOWN_STATUS
      // Unknown is the absence of an answer — keep it out of the ranking.
      if (status === UNKNOWN_STATUS) return null
      return statusSortRank[status] ?? 99
    }
    case 'holds': {
      // Group by the leading hold: same reason, same block. The tag array is
      // the fallback for rows written before hold details existed.
      const primary = m.latest_hold_details?.[0]
      if (primary) return `${holdAbbr[primary.category] || primary.category}|${primary.label}`
      const legacy = m.latest_holds?.[0]
      return legacy ? holdAbbr[legacy] || legacy : null
    }
    case 'client':
      return m.client_name || null
    case 'terminal':
      return m.terminal_site ? terminalLabels[m.terminal_site] || m.terminal_site : null
    case 'vessel':
      return m.latest_vessel || null
    case 'checked':
      return m.last_checked_at ? new Date(m.last_checked_at).getTime() : null
  }
}

function sortMonitors(rows: ContainerMonitor[], key: SortKey, dir: SortDir): ContainerMonitor[] {
  const factor = dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const va = sortValue(a, key)
    const vb = sortValue(b, key)
    if (va === null && vb === null) return 0
    if (va === null) return 1
    if (vb === null) return -1
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor
    return String(va).localeCompare(String(vb)) * factor
  })
}

/**
 * A clickable column header. Three states on repeated clicks: ascending,
 * descending, then off — "off" restores the backend's urgent-first ordering,
 * which is the view most dispatchers want to come back to.
 */
const SortableTh: React.FC<{
  label: string
  sortKey: SortKey
  active: SortKey | null
  dir: SortDir
  onSort: (key: SortKey) => void
  className?: string
}> = ({ label, sortKey, active, dir, onSort, className = '' }) => {
  const isActive = active === sortKey
  const Icon = !isActive ? ChevronsUpDown : dir === 'asc' ? ChevronUp : ChevronDown
  return (
    <th
      className={`px-3 py-2.5 text-left font-semibold text-slate-600 ${className}`}
      aria-sort={isActive ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={`Sort by ${label}`}
        className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${
          isActive ? 'text-[#00838F]' : ''
        }`}
      >
        {label}
        <Icon className={`w-3 h-3 ${isActive ? 'opacity-100' : 'opacity-30'}`} />
      </button>
    </th>
  )
}

// ==================== Main Component ====================

export const ContainerMonitorModal: React.FC<ContainerMonitorModalProps> = ({ open, onClose }) => {
  const {
    monitors,
    stats,
    recentChanges,
    loading,
    refreshAll,
    refreshSingle,
    refreshSelected,
    checkPcc,
    stopMonitor,
    batchStopMonitors,
    startMonitor,
    fetchHistory,
  } = useContainerMonitor({ enabled: open })

  // Display only — ExtensionBridge (mounted app-wide) owns enrolment now.
  // Two instances enrolling would run two retry ladders at the same extension.
  const ext = useExtensionEnrollment({ autoEnroll: false })
  // Single source of truth: socket.io pushes presence on 0↔1 transitions; a 30s
  // HTTP poll refreshes `sessions` (SSA/ETS/TraPac health) and defends against
  // missed events. Starts at `online === null` ("Checking...") so the cold-start
  // race window no longer flashes red for up to 15s every morning.
  const {
    online: backendOnline,
    sessions: extensionSessions,
    siteKeys: extensionSiteKeys,
    recheckSession,
    savePccKey,
    clearPccKey,
  } = useExtensionLiveStatus({ enabled: open })

  // Opens the shared Task Detail modal from a container number.
  const { onEntityClick, taskModalOpen } = useEntityLink()

  const [searchText, setSearchText] = useState('')
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [terminalFilter, setTerminalFilter] = useState<string>('all')
  const [alertFilter, setAlertFilter] = useState<string>('all')
  const [monitorStatusFilter, setMonitorStatusFilter] = useState<string>('active')
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [refreshingId, setRefreshingId] = useState<number | null>(null)
  const [refreshingAll, setRefreshingAll] = useState(false)
  // PCC check: which row is waiting, and what the last answer was.
  // The answer is kept per row rather than as one page-level banner — the most
  // common answer ("PCC has no record yet") changes nothing on the row, so it
  // has to sit next to the container it is about or it reads as belonging to
  // whichever row the eye lands on.
  const [pccCheckingId, setPccCheckingId] = useState<number | null>(null)
  const [pccNotes, setPccNotes] = useState<Record<number, { ok: boolean; text: string }>>({})
  // History panel
  const [historyMonitorId, setHistoryMonitorId] = useState<number | null>(null)
  const [historyLogs, setHistoryLogs] = useState<StatusChangeLog[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  // Start monitor form
  const [showStartForm, setShowStartForm] = useState(false)
  const [startContainer, setStartContainer] = useState('')
  const [startOrderId, setStartOrderId] = useState('')
  const [startingMonitor, setStartingMonitor] = useState(false)

  // 选中的是 CBP Exam 那一组吗？这一组自己就横跨 active / stopped 两种监控。
  const examFilterActive = terminalFilter === EXAM_FILTER || terminalFilter === PCC_FILTER

  // Filtered monitors
  const filteredMonitors = useMemo(() => {
    return monitors.filter(m => {
      if (searchText && !m.container_number.toLowerCase().includes(searchText.toLowerCase())) return false
      if (statusFilter !== 'all' && (m.latest_status || UNKNOWN_STATUS) !== statusFilter) return false
      if (terminalFilter === UNKNOWN_TERMINAL) {
        if (m.terminal_site) return false
      } else if (terminalFilter === EXAM_FILTER) {
        // Everything CBP pulled for exam, confirmed at PCC or not.
        if (!isExamPile(m)) return false
      } else if (terminalFilter === PCC_FILTER) {
        // At the exam station. A PCC record with no dates on it is PCC having
        // opened a file, not PCC having the box — see pcc_found_at.
        if (!isAtPcc(m)) return false
      } else if (terminalFilter !== 'all' && m.terminal_site !== terminalFilter) {
        return false
      }
      if (alertFilter !== 'all' && m.lfd_alert_level !== alertFilter) return false
      // 监控状态那一栏对查验柜不适用：进了 CBP Hold 就一定是 stopped，拿
      // 「Active」去卡等于把这两项永远筛成空的。
      if (!examFilterActive && monitorStatusFilter !== 'all' && m.monitor_status !== monitorStatusFilter) return false
      return true
    })
  }, [monitors, searchText, statusFilter, terminalFilter, alertFilter, monitorStatusFilter, examFilterActive])

  // No sort column = keep whatever order the backend sent (urgent first).
  const visibleMonitors = useMemo(
    () => (sortKey ? sortMonitors(filteredMonitors, sortKey, sortDir) : filteredMonitors),
    [filteredMonitors, sortKey, sortDir]
  )

  // asc → desc → off (back to the backend's urgent-first order)
  const handleSort = useCallback((key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key)
      setSortDir('asc')
      return
    }
    if (sortDir === 'asc') {
      setSortDir('desc')
      return
    }
    setSortKey(null)
    setSortDir('asc')
  }, [sortKey, sortDir])

  const handleOpenTask = useCallback((taskId: number) => {
    onEntityClick('task', taskId)
  }, [onEntityClick])

  const handleRefreshAll = async () => {
    setRefreshingAll(true)
    await refreshAll()
    setRefreshingAll(false)
  }

  const handleRefreshSingle = async (id: number) => {
    setRefreshingId(id)
    await refreshSingle(id)
    setRefreshingId(null)
  }

  // The modal keeps its state while closed, and a PCC answer is only true for
  // the moment it was given — "no record yet" from yesterday must not greet
  // whoever opens this window tomorrow.
  useEffect(() => {
    if (!open) setPccNotes({})
  }, [open])

  const handlePccCheck = useCallback(async (id: number) => {
    setPccCheckingId(id)
    const outcome = await checkPcc(id)
    setPccNotes(prev => ({ ...prev, [id]: { ok: outcome.ok, text: outcome.message } }))
    // Only clear the spinner if it is still ours: two rows can be in flight at
    // once, and the first one back must not un-spin the second.
    setPccCheckingId(prev => (prev === id ? null : prev))
  }, [checkPcc])

  const handleRefreshSelected = async () => {
    if (selectedIds.size === 0) return
    setRefreshingAll(true)
    await refreshSelected(Array.from(selectedIds))
    setSelectedIds(new Set())
    setRefreshingAll(false)
  }

  const handleStopSelected = async () => {
    if (selectedIds.size === 0) return
    await batchStopMonitors(Array.from(selectedIds), 'manual')
    setSelectedIds(new Set())
  }

  const handleStopSingle = async (id: number) => {
    await stopMonitor(id, 'manual')
  }

  const handleViewHistory = async (monitorId: number) => {
    setHistoryMonitorId(monitorId)
    setHistoryLoading(true)
    const logs = await fetchHistory(monitorId)
    setHistoryLogs(logs)
    setHistoryLoading(false)
  }

  const handleStartMonitor = async () => {
    if (!startContainer.trim()) return
    setStartingMonitor(true)
    await startMonitor(startContainer.trim(), startOrderId.trim() || undefined)
    setStartContainer('')
    setStartOrderId('')
    setShowStartForm(false)
    setStartingMonitor(false)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredMonitors.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredMonitors.map(m => m.id)))
    }
  }

  if (!open) return null

  // History detail view
  const historyMonitor = historyMonitorId ? monitors.find(m => m.id === historyMonitorId) : null

  return createPortal(
    <div
      className={`fixed inset-0 flex items-center justify-center ${
        // Step below the Task Detail modal (z-50) while it's open — it portals
        // to <body>, so staying at z-100 would bury the detail we just opened.
        taskModalOpen ? 'z-40' : 'z-[100]'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1200px] max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            {historyMonitorId ? (
              <button
                onClick={() => setHistoryMonitorId(null)}
                className="p-1 rounded hover:bg-slate-200 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
            ) : (
              <div className="w-8 h-8 bg-[#00838F] rounded-lg flex items-center justify-center">
                <Ship className="w-4 h-4 text-white" />
              </div>
            )}
            <h2 className="text-base font-bold text-slate-900">
              {historyMonitorId
                ? `History — ${historyMonitor?.container_number || ''}`
                : 'Container Monitor'
              }
            </h2>
            {!historyMonitorId && stats && (
              <div className="flex items-center gap-2 ml-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                  {stats.released_count} Released
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                  {stats.on_hold_count} Hold
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                  {stats.on_vessel_count} Vessel
                </span>
                {parseInt(stats.unknown_count) > 0 && (
                  <span
                    className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 font-medium"
                    title={'Containers with no reading for this voyage. Mixed pile — the Status'
                      + ' column says which kind each one is: Not on file, Last trip only, or'
                      + ' nothing read yet.'}
                  >
                    {stats.unknown_count} Unknown
                  </span>
                )}
                {parseInt(stats.urgent_count) > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium animate-pulse">
                    {stats.urgent_count} Urgent
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Extension status badge.
                Priority (first match wins):
                  1. extension not installed → "No Extension" (gray)
                  2. installed but never enrolled → "Not Enrolled" (amber)
                  3. backend hasn't answered yet → "Checking..." (gray)
                  4. backend says online → "Online" (green)
                  5. backend says offline → "Offline — Reconnecting..." (red)
                `null` no longer falls through to "Not Enrolled" — we keep it
                distinct so cold-start never shows the wrong amber label. */}
            {(() => {
              const { label, cls } = !ext.installed
                ? { label: 'No Extension', cls: 'bg-slate-100 text-slate-500' }
                : !ext.enrolled
                ? { label: '● Extension Not Enrolled', cls: 'bg-amber-100 text-amber-700' }
                : backendOnline === null
                ? { label: '● Checking connection…', cls: 'bg-slate-100 text-slate-500' }
                : backendOnline === true
                ? { label: '● Extension Online', cls: 'bg-emerald-100 text-emerald-700' }
                : { label: '● Extension Offline — Reconnecting…', cls: 'bg-red-100 text-red-700' }
              return (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>
              )
            })()}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </div>

        {historyMonitorId ? (
          /* ==================== History Detail View ==================== */
          <div className="flex-1 overflow-auto">
            {historyLoading ? (
              <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
                Loading history...
              </div>
            ) : historyLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <History className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">No status changes recorded yet</p>
              </div>
            ) : (
              <div className="px-5 py-3">
                {/* Monitor summary */}
                {historyMonitor && (
                  <div className="flex items-center gap-4 mb-4 p-3 bg-slate-50 rounded-lg text-xs">
                    <div>
                      <span className="text-slate-500">Container:</span>{' '}
                      <span className="font-mono font-semibold">{historyMonitor.container_number}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Order:</span>{' '}
                      <span className="font-medium">{historyMonitor.order_id || '-'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Terminal:</span>{' '}
                      <span className="font-medium">{terminalLabels[historyMonitor.terminal_site || ''] || historyMonitor.terminal_site || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Status:</span>{' '}
                      <span
                        className={`font-medium ${describeStatus(historyMonitor).text}`}
                        title={describeStatus(historyMonitor).title || undefined}
                      >
                        {describeStatus(historyMonitor).label}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Checks:</span>{' '}
                      <span className="font-medium">{historyMonitor.check_count || 0}</span>
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="space-y-0">
                  {historyLogs.map((log, idx) => (
                    <div key={log.id} className="flex gap-3 relative">
                      {/* Connector line */}
                      {idx < historyLogs.length - 1 && (
                        <div className="absolute left-[11px] top-6 bottom-0 w-px bg-slate-200" />
                      )}
                      {/* Dot */}
                      <div className={`w-[23px] h-[23px] rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 z-10 bg-white ${
                        log.old_status !== log.new_status ? 'border-blue-500' : 'border-slate-300'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          log.old_status !== log.new_status ? 'bg-blue-500' : 'bg-slate-300'
                        }`} />
                      </div>
                      {/* Content */}
                      <div className="flex-1 pb-4 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400">{formatDateTime(log.checked_at)}</span>
                          <span className="text-slate-400">({terminalLabels[log.terminal_site || ''] || log.terminal_site || '-'})</span>
                        </div>
                        <div className="mt-0.5 space-y-0.5">
                          {log.old_status && log.new_status && (
                            <div>
                              <span className="text-slate-500">Status:</span>{' '}
                              <span className="font-medium">{log.old_status}</span>
                              <span className="text-slate-400"> → </span>
                              <span className="font-medium text-blue-700">{log.new_status}</span>
                            </div>
                          )}
                          {log.old_holds && log.new_holds && (
                            <div>
                              <span className="text-slate-500">Holds:</span>{' '}
                              <span className="font-medium">{formatHolds(log.old_holds)}</span>
                              <span className="text-slate-400"> → </span>
                              <span className="font-medium">{formatHolds(log.new_holds)}</span>
                            </div>
                          )}
                          {/* The reasons behind the new tags, in the terminal's
                              own wording — a tag diff alone doesn't say why. */}
                          {log.new_hold_details && log.new_hold_details.length > 0 && (
                            <ul className="mt-0.5 space-y-0.5">
                              {log.new_hold_details.map((d, i) => (
                                <li key={`${d.category}-${d.code ?? i}`} className="text-slate-600">
                                  <span className={`inline-flex items-center px-1 py-0.5 mr-1 rounded border text-[10px] font-medium ${
                                    holdCategoryStyle[d.category] || 'bg-slate-50 text-slate-600 border-slate-200'
                                  }`}>
                                    {holdAbbr[d.category] || d.category}
                                  </span>
                                  {d.label}
                                  {d.detail && <span className="text-slate-400"> — {d.detail}</span>}
                                </li>
                              ))}
                            </ul>
                          )}
                          {lfdDateOnly(log.old_lfd) !== lfdDateOnly(log.new_lfd) && (log.old_lfd || log.new_lfd) && (
                            <div>
                              <span className="text-slate-500">LFD:</span>{' '}
                              <span className="font-medium">{formatLfdShort(log.old_lfd)}</span>
                              <span className="text-slate-400"> → </span>
                              <span className="font-medium text-blue-700">{formatLfdShort(log.new_lfd)}</span>
                            </div>
                          )}
                          {!log.old_status && !log.new_status && !log.old_holds && !log.new_holds
                            && !log.new_hold_details?.length && lfdDateOnly(log.old_lfd) === lfdDateOnly(log.new_lfd) && (
                            <div className="text-slate-400 italic">No changes detected</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* ==================== Filters ==================== */}
            <div className="flex items-center gap-2 px-5 py-2.5 border-b border-slate-100 bg-white">
              <div className="relative flex-1 max-w-[240px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search container..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00838F] focus:border-[#00838F]"
                />
              </div>

              {/* 灰掉而不是偷偷忽略：CBP Exam 那一组横跨 active/stopped，
                  让这个框还显示「Active」却不生效，下一个人就会以为漏了柜子。 */}
              <Select
                value={examFilterActive ? 'all' : monitorStatusFilter}
                onChange={v => setMonitorStatusFilter(v)}
                disabled={examFilterActive}
                title={examFilterActive ? 'Containers leave monitoring once CBP Exam becomes CBP Hold — this group ignores the monitoring filter' : undefined}
                size="small"
                style={{ width: 100 }}
                getPopupContainer={(trigger) => trigger.closest('[role="dialog"]') as HTMLElement || document.body}
              >
                <Select.Option value="active">Active</Select.Option>
                <Select.Option value="stopped">Stopped</Select.Option>
                <Select.Option value="all">All</Select.Option>
              </Select>

              <Select
                value={statusFilter}
                onChange={v => setStatusFilter(v)}
                size="small"
                style={{ width: 120 }}
                getPopupContainer={(trigger) => trigger.closest('[role="dialog"]') as HTMLElement || document.body}
              >
                <Select.Option value="all">All Status</Select.Option>
                <Select.Option value="released">Released</Select.Option>
                <Select.Option value="on-hold">On Hold</Select.Option>
                <Select.Option value="on-vessel">On Vessel</Select.Option>
                <Select.Option value="picked-up">Picked Up</Select.Option>
                <Select.Option value={UNKNOWN_STATUS}>Unknown</Select.Option>
              </Select>

              <Select
                value={terminalFilter}
                onChange={v => setTerminalFilter(v)}
                size="small"
                style={{ width: 168 }}
                getPopupContainer={(trigger) => trigger.closest('[role="dialog"]') as HTMLElement || document.body}
              >
                <Select.Option value="all">All Terminals</Select.Option>
                <Select.OptGroup label="Terminals">
                  <Select.Option value="oict">SSA OICT</Select.Option>
                  <Select.Option value="ets">ETS</Select.Option>
                  <Select.Option value="trapac">TraPac</Select.Option>
                  <Select.Option value={UNKNOWN_TERMINAL}>Unknown</Select.Option>
                </Select.OptGroup>
                {/* Its own group because neither of these is a terminal: the
                    box left the terminal when CBP pulled it. Grouping also
                    makes the pair readable as two stages of one thing —
                    pulled for exam, then confirmed at the exam station. */}
                <Select.OptGroup label="CBP Exam">
                  <Select.Option value={EXAM_FILTER}>Transfer for Exam</Select.Option>
                  <Select.Option value={PCC_FILTER}>Confirmed at PCC</Select.Option>
                </Select.OptGroup>
              </Select>

              <Select
                value={alertFilter}
                onChange={v => setAlertFilter(v)}
                size="small"
                style={{ width: 120 }}
                getPopupContainer={(trigger) => trigger.closest('[role="dialog"]') as HTMLElement || document.body}
              >
                <Select.Option value="all">All Alerts</Select.Option>
                <Select.Option value="overdue">Overdue</Select.Option>
                <Select.Option value="critical">Critical</Select.Option>
                <Select.Option value="warning">Warning</Select.Option>
                <Select.Option value="normal">Normal</Select.Option>
              </Select>

              <div className="flex-1" />

              {/* Batch action buttons */}
              {selectedIds.size > 0 && (
                <>
                  <button
                    onClick={handleStopSelected}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-red-300 text-red-600 rounded-md hover:bg-red-50"
                  >
                    <StopCircle className="w-3 h-3" />
                    Stop ({selectedIds.size})
                  </button>
                  <button
                    onClick={handleRefreshSelected}
                    disabled={refreshingAll}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#00838F] text-white rounded-md hover:bg-[#006064] disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${refreshingAll ? 'animate-spin' : ''}`} />
                    Check ({selectedIds.size})
                  </button>
                </>
              )}

              {/* Start Monitor button */}
              <button
                onClick={() => setShowStartForm(!showStartForm)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-emerald-300 text-emerald-700 rounded-md hover:bg-emerald-50"
              >
                <Plus className="w-3 h-3" />
                Add
              </button>

              <button
                onClick={handleRefreshAll}
                disabled={refreshingAll || loading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${refreshingAll || loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {/* Start Monitor Form (inline) */}
            {showStartForm && (
              <div className="flex items-center gap-2 px-5 py-2 border-b border-slate-100 bg-emerald-50/50">
                <input
                  type="text"
                  placeholder="Container # (e.g. OOLU1234567)"
                  value={startContainer}
                  onChange={e => setStartContainer(e.target.value.toUpperCase())}
                  className="px-2 py-1.5 text-xs border border-slate-200 rounded-md w-[200px] font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <input
                  type="text"
                  placeholder="Order ID (optional)"
                  value={startOrderId}
                  onChange={e => setStartOrderId(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-slate-200 rounded-md w-[140px] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <button
                  onClick={handleStartMonitor}
                  disabled={!startContainer.trim() || startingMonitor}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                  <Play className="w-3 h-3" />
                  {startingMonitor ? 'Starting...' : 'Start Monitoring'}
                </button>
                <button
                  onClick={() => setShowStartForm(false)}
                  className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* ==================== Table ==================== */}
            <div className="flex-1 overflow-auto">
              {loading && monitors.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
                  Loading monitors...
                </div>
              ) : filteredMonitors.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                  <Ship className="w-10 h-10 mb-3 opacity-30" />
                  {/* "Nothing is being monitored" and "nothing matches your
                      filter" are different facts, and saying the first when the
                      second is true sends people off looking for a broken
                      monitor. */}
                  {monitors.length > 0 ? (
                    <>
                      <p className="text-sm">No containers match these filters</p>
                      <p className="text-xs mt-1">{monitors.length} monitored container{monitors.length === 1 ? '' : 's'} hidden — clear a filter to see them</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">No containers being monitored</p>
                      <p className="text-xs mt-1">Containers from Import Orders will appear here automatically</p>
                    </>
                  )}
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr className="border-b border-slate-200">
                      <th className="w-8 px-2 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.size === filteredMonitors.length && filteredMonitors.length > 0}
                          onChange={toggleSelectAll}
                          className="w-3.5 h-3.5 rounded border-slate-300"
                        />
                      </th>
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-600">Container</th>
                      <SortableTh label="Status" sortKey="status" active={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh label="Holds" sortKey="holds" active={sortKey} dir={sortDir} onSort={handleSort} />
                      <th className="px-3 py-2.5 text-left font-semibold text-slate-600">LFD</th>
                      <SortableTh label="Client" sortKey="client" active={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh label="Terminal" sortKey="terminal" active={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh label="Vessel" sortKey="vessel" active={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh label="Checked" sortKey="checked" active={sortKey} dir={sortDir} onSort={handleSort} />
                      {/* One notch wider than it used to be: exam rows carry a
                          fourth control (Ask PCC), and at w-24 the buttons
                          reflowed onto two lines for those rows only. */}
                      <th className="w-28 px-2 py-2.5 text-center font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleMonitors.map(monitor => (
                      <MonitorRow
                        key={monitor.id}
                        monitor={monitor}
                        selected={selectedIds.has(monitor.id)}
                        onToggleSelect={() => toggleSelect(monitor.id)}
                        refreshing={refreshingId === monitor.id}
                        onRefresh={() => handleRefreshSingle(monitor.id)}
                        pccChecking={pccCheckingId === monitor.id}
                        pccNote={pccNotes[monitor.id]}
                        onPccCheck={() => handlePccCheck(monitor.id)}
                        onStop={() => handleStopSingle(monitor.id)}
                        onViewHistory={() => handleViewHistory(monitor.id)}
                        onOpenTask={handleOpenTask}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ==================== Footer: Recent Changes + Cookie Status ==================== */}
            <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex gap-6 text-xs shrink-0 max-h-[160px] overflow-auto">
              {/* Recent Changes */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-slate-600 mb-1.5">Recent Changes</h4>
                {recentChanges.length === 0 ? (
                  <p className="text-slate-400 italic">No recent changes</p>
                ) : (
                  <div className="space-y-1">
                    {recentChanges.slice(0, 3).map(change => (
                      <RecentChangeRow key={change.id} change={change} />
                    ))}
                  </div>
                )}
              </div>

              {/* Extension Session Status */}
              <div className="w-[280px] shrink-0">
                <h4 className="font-semibold text-slate-600 mb-1.5">
                  {ext.enrolled ? 'Extension Status' : 'Connection Status'}
                </h4>
                <div className="space-y-1">
                  {ext.checked && !ext.installed && (
                    <div className="text-xs text-slate-500">
                      No extension installed.
                      <br />
                      <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}>
                        Install Extension
                      </span>
                    </div>
                  )}
                  {ext.installed && !ext.enrolled && (
                    <div className="text-xs text-amber-600">
                      Extension installed but not connected.
                      <br />
                      <span className="text-blue-600 cursor-pointer hover:underline" onClick={() => ext.enrollExtension()}>
                        Click to connect
                      </span>
                    </div>
                  )}
                  {ext.enrolled && (
                    <div className="text-xs space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                        <span className="text-slate-600">Extension v{ext.version} connected</span>
                      </div>
                      <ExtensionSessionStatus
                        sessions={extensionSessions}
                        siteKeys={extensionSiteKeys}
                        onRecheck={recheckSession}
                        onSavePccKey={savePccKey}
                        onClearPccKey={clearPccKey}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  )
}

// ==================== Sub-components ====================

const MonitorRow: React.FC<{
  monitor: ContainerMonitor
  selected: boolean
  onToggleSelect: () => void
  refreshing: boolean
  onRefresh: () => void
  pccChecking: boolean
  pccNote?: { ok: boolean; text: string }
  onPccCheck: () => void
  onStop: () => void
  onViewHistory: () => void
  onOpenTask: (taskId: number) => void
}> = React.memo(({ monitor, selected, onToggleSelect, refreshing, onRefresh, pccChecking, pccNote, onPccCheck, onStop, onViewHistory, onOpenTask }) => {
  const alert = (monitor.lfd_alert_level as LfdAlertLevel) || 'normal'
  const isStopped = monitor.monitor_status === 'stopped'
  // Customs pulled this one — so there are two places to ask about it, and two
  // buttons. Unlike the terminal button this one survives `isStopped`: a
  // container in CBP Hold is exactly the case where the terminal has gone
  // quiet and PCC is the only one still answering.
  const canAskPcc = isExamPile(monitor)
  const statusStyle = describeStatus(monitor)
  const alertStyle = alertColors[alert] || alertColors['normal']

  return (
    <tr
      className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${
        isStopped ? 'opacity-50' : alert === 'overdue' || alert === 'critical' ? alertStyle.bg : ''
      }`}
    >
      <td className="px-2 py-2 text-center">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="w-3.5 h-3.5 rounded border-slate-300"
        />
      </td>
      <td className="px-3 py-2">
        {/* A monitor row can exist before a task does (or after the task is
            gone), so only link when we actually have one to open. */}
        {monitor.task_id ? (
          <button
            type="button"
            onClick={() => onOpenTask(monitor.task_id as number)}
            title="Open task details"
            className="font-mono font-medium text-[#00838F] hover:text-[#006064] hover:underline cursor-pointer"
          >
            {monitor.container_number}
          </button>
        ) : (
          <span className="font-mono font-medium text-slate-900" title="No task linked to this container yet">
            {monitor.container_number}
          </span>
        )}
        {monitor.order_id && (
          <span className="ml-1.5 text-[10px] text-slate-400">{monitor.order_id}</span>
        )}
        {isStopped && (
          <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-slate-200 text-slate-500">
            {monitor.stop_reason || 'stopped'}
          </span>
        )}
        <PccDatesLine record={monitor.pcc_data} />
        {pccNote && (
          <div className={`mt-0.5 text-[10px] ${pccNote.ok ? 'text-slate-500' : 'text-red-600'}`}>
            PCC: {pccNote.text}
          </div>
        )}
      </td>
      <td className="px-3 py-2">
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${statusStyle.bg} ${statusStyle.text}`}
          title={statusStyle.title || undefined}
        >
          {statusStyle.label}
        </span>
      </td>
      <td className="px-3 py-2 text-slate-600">
        <HoldReasons
          details={monitor.latest_hold_details}
          holds={monitor.latest_holds}
          terminalHoldCodes={monitor.latest_terminal_hold_codes}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <div className={`w-2 h-2 rounded-full ${alertStyle.dot}`} />
          <span className={alertStyle.text}>
            {formatLfdShort(monitor.latest_lfd)}
          </span>
          {alert === 'overdue' && (
            <span className="text-[10px] text-red-600 font-medium">+{monitor.demurrage_days}d</span>
          )}
        </div>
      </td>
      {/* 谁的柜子 = 订单的 Ord. Party，和派车板那列读同一个值。监控行可以先于订单
          存在（或订单已不在本租户），那时没名字可写。 */}
      <td
        className="px-3 py-2 text-slate-600 truncate max-w-[140px]"
        title={monitor.client_name || ''}
      >
        {monitor.client_name || <span className="text-slate-400">-</span>}
      </td>
      <td className="px-3 py-2 text-slate-600">
        <div className="flex items-center gap-1.5">
          {monitor.terminal_site
            ? terminalLabels[monitor.terminal_site] || monitor.terminal_site
            : <span className="text-slate-400 italic">Unknown</span>
          }
          {/* Shown ALONGSIDE the terminal, never instead of it: the container
              came off a terminal and is now at the exam station, and both
              facts stay true until it comes back. */}
          <PccChip monitor={monitor} />
        </div>
      </td>
      <td className="px-3 py-2 text-slate-600 truncate max-w-[120px]" title={monitor.latest_vessel || ''}>
        {monitor.latest_vessel || '-'}
      </td>
      <td className="px-3 py-2 text-slate-500">
        {formatTime(monitor.last_checked_at)}
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center justify-center gap-0.5">
          <button
            onClick={onViewHistory}
            className="p-1 rounded hover:bg-slate-200 transition-colors"
            title="View history"
          >
            <History className="w-3.5 h-3.5 text-slate-500" />
          </button>
          {canAskPcc && (
            <button
              onClick={onPccCheck}
              disabled={pccChecking}
              className="p-1 rounded hover:bg-amber-100 transition-colors disabled:opacity-40"
              title="Ask PCC now — has the exam station got this container?"
            >
              {/* Same icon and size as the terminal refresh beside it; the
                  colour and the tooltip are what tell them apart. */}
              <RefreshCw className={`w-3.5 h-3.5 text-amber-600 ${pccChecking ? 'animate-spin' : ''}`} />
            </button>
          )}
          {!isStopped && (
            <>
              <button
                onClick={onRefresh}
                disabled={refreshing}
                className="p-1 rounded hover:bg-slate-200 transition-colors disabled:opacity-40"
                title="Check now — asks the terminal"
              >
                <RefreshCw className={`w-3.5 h-3.5 text-slate-500 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onStop}
                className="p-1 rounded hover:bg-red-100 transition-colors"
                title="Stop monitoring"
              >
                <StopCircle className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  )
}, (prev, next) =>
  prev.monitor === next.monitor
  && prev.selected === next.selected
  && prev.refreshing === next.refreshing
  && prev.pccChecking === next.pccChecking
  && prev.pccNote === next.pccNote
)

const RecentChangeRow: React.FC<{ change: StatusChangeLog }> = ({ change }) => {
  const time = formatTime(change.checked_at)

  let description = ''
  if (change.old_status && change.new_status) {
    description = `${change.old_status} → ${change.new_status}`
  } else if (change.old_holds && change.new_holds) {
    description = `holds: ${formatHolds(change.new_holds)}`
  } else if (lfdDateOnly(change.old_lfd) !== lfdDateOnly(change.new_lfd)) {
    description = `LFD: ${formatLfdShort(change.old_lfd)} → ${formatLfdShort(change.new_lfd)}`
  }

  return (
    <div className="flex items-center gap-2 text-slate-500">
      <span className="text-slate-400 w-10 shrink-0">{time}</span>
      <span className="font-mono text-slate-700">{change.container_number}</span>
      <span>{description}</span>
      {change.terminal_site && (
        <span className="text-slate-400">({terminalLabels[change.terminal_site] || change.terminal_site})</span>
      )}
    </div>
  )
}

// ==================== PCC (customs exam station) ====================

/**
 * The PCC marker in the Terminal column.
 *
 *   nothing      — we have never read a PCC record for this container
 *   "PCC"        — PCC has this shipment on file. Since 2026-09-24 that alone
 *                  is what pushes the task to CBP Hold; the milestone dates
 *                  below the container number say how far along it is.
 *   "PCC ?"      — we read a record but never marked it found. Only reachable
 *                  for rows written under the old rule (dates required), or
 *                  when the container is not in the exam pile at all, so the
 *                  status push was deliberately withheld.
 */
const PccChip: React.FC<{ monitor: ContainerMonitor }> = ({ monitor }) => {
  const record = monitor.pcc_data
  if (!record) return null

  const confirmed = !!monitor.pcc_found_at
  const title = confirmed
    ? `Found at PCC ${formatDateTime(monitor.pcc_found_at)}`
    : 'PCC has a file for this container, but it was never marked found — check it again'

  return (
    <span
      title={title}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
        confirmed
          ? 'bg-amber-100 text-amber-800'
          : 'bg-slate-100 text-slate-500'
      }`}
    >
      PCC{confirmed ? '' : ' ?'}
    </span>
  )
}

/**
 * The milestone dates PCC published, under the container number.
 *
 * Only the ones PCC has actually filled in — an eight-slot skeleton of dashes
 * would take three lines to say nothing. Green (completed) is PCC's own
 * legend, carried over so the row reads the same way their page does.
 */
const PccDatesLine: React.FC<{ record: PccRecord | null }> = ({ record }) => {
  if (!record) return null
  const filled = PCC_DATE_ORDER.filter((d) => record.dates?.[d.key])
  if (filled.length === 0) return null

  return (
    // No "PCC" caption here — the chip in the Terminal column already says so,
    // and repeating it inside the row made the word ambiguous on screen as
    // well as in tests.
    <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] text-slate-500">
      {filled.map((d) => {
        const done = (record.completed || []).includes(d.key)
        return (
          <span key={d.key} title={d.label}>
            <span className={done ? 'text-emerald-700 font-medium' : 'text-slate-400'}>{d.short}</span>
            {' '}
            <span className="font-mono">{monthDay(record.dates[d.key])}</span>
          </span>
        )
      })}
    </div>
  )
}

/** "2026-09-18" -> "09/18". Dates from PCC are already normalised ISO. */
function monthDay(iso: string | null): string {
  if (!iso) return '-'
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  // Anything the parser could not normalise is shown as PCC wrote it, rather
  // than guessed at — see normalizeDate in pcc.parser.js.
  return m ? `${m[2]}/${m[3]}` : iso
}

// ==================== Extension Session Status ====================

const SESSION_LABELS: Record<string, string> = { oict: 'SSA', ets: 'ETS', trapac: 'TraPac', pcc: 'PCC' }

// Where each terminal's sign-in lives. Clicking the inline "Login" button opens
// the URL in a new tab; the installed content scripts pick up the refreshed
// _sk / cookies on successful auth.
//
// The button used to stop there, which made it look broken: the panel kept
// reading "Session expired" until the next 30s poll happened to catch a
// successful scrape. Now the row goes into a "waiting for sign-in" state and
// asks the backend to run a real check the moment the user comes back to this
// tab (see SessionRow).
const LOGIN_URLS: Record<string, string> = {
  oict: 'https://b58.tideworks.com/fc-OICT/default.do',
  ets:  'https://www.etslink.com/menu.html',
}

// TraPac is public — there is no account and nothing to sign into. It had a
// Login button anyway, permanently reading "Not logged in yet", which sent
// dispatchers hunting for credentials that don't exist.
// PCC joins TraPac here for a different reason: there is no account at all,
// but there IS a per-tenant link that acts as one. That link is handled by its
// own row below (PccKeyRow) rather than by a Login button, because pasting a
// link is not signing in — and unlike a terminal login, one dispatcher pasting
// it fixes it for the whole company.
const NO_LOGIN_SITES = ['trapac', 'pcc']

// How long after clicking Login we keep auto-checking, and how often.
// Each check is a real query against the terminal, so the backstop is
// deliberately slow — window focus is the primary signal and covers the normal
// path. This caps an unattended login attempt at 4 extra terminal queries.
const AWAIT_LOGIN_WINDOW_MS = 2 * 60 * 1000
const AWAIT_LOGIN_POLL_MS = 30000

// Freshness thresholds: a session row with status='ok' is only trusted as
// "Active" while the last successful scrape is recent. Past these thresholds
// we degrade to Stale / Unverified so the UI can't lie about a dead session.
const FRESH_MS = 60 * 60 * 1000       // 1h  → "Active"
const STALE_MS = 6 * 60 * 60 * 1000   // 6h  → "Stale"

type SessionInfo = ExtensionSessionInfo

function formatAge(ms: number): string {
  const m = Math.floor(ms / 60000)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

type SessionDisplay = {
  dot: string
  color: string
  label: string
  /** When true, surface a Login button — session can't be trusted for scraping. */
  needsLogin: boolean
}

/**
 * What the row should say.
 *
 * A tenant runs several dispatchers, each with their own machine and their own
 * terminal login, so the only question worth answering here is **"is container
 * monitoring working?"** — which is a rollup, not one machine's opinion. Green
 * while ANY machine can read the terminal; red only when none can.
 *
 * That also dissolves the old ambiguity. "Session expired" on a shared panel
 * left five dispatchers wondering which of them was supposed to go log in, so
 * none of them did. Red now means *nobody* is signed in, which makes the
 * Login button unambiguous: whoever is looking at it can fix it, in their own
 * browser, which is the only browser they could sign into anyway.
 */
function deriveSessionDisplay(siteId: string, info: SessionInfo, keyConfigured?: boolean): SessionDisplay {
  // PCC has no login, but it does have a per-tenant link that acts as the
  // account. Without it there is nothing to be green about, however many
  // machines are connected — so the key state outranks the machine rollup.
  if (siteId === 'pcc' && keyConfigured === false) {
    return { dot: 'bg-slate-300', color: 'text-slate-400', label: 'No PCC link on file — exam containers are not being checked', needsLogin: false }
  }
  if (siteId === 'pcc') {
    const ok = info.machines_ok
    const total = info.machines_total
    if (typeof ok === 'number' && typeof total === 'number' && total > 0 && ok === 0) {
      // Almost always the fleet being half-upgraded: reading PCC needs an
      // extension new enough to be allowed to fetch that host.
      return { dot: 'bg-amber-500', color: 'text-amber-600', label: `Link on file, but none of ${total} machine${total === 1 ? '' : 's'} can reach PCC — update the extension`, needsLogin: false }
    }
    const age = info.last_success_at ? Date.now() - new Date(info.last_success_at).getTime() : null
    if (age === null) {
      return { dot: 'bg-slate-300', color: 'text-slate-400', label: 'Link on file — not checked yet', needsLogin: false }
    }
    return {
      dot: 'bg-emerald-500',
      color: 'text-emerald-600',
      label: `Working — last read ${formatAge(age)}`,
      needsLogin: false,
    }
  }

  if (NO_LOGIN_SITES.includes(siteId)) {
    return { dot: 'bg-slate-300', color: 'text-slate-400', label: 'Public — no login needed', needsLogin: false }
  }

  const total = info.machines_total
  const ok = info.machines_ok
  // `machines_*` arrive only from the channel-pool backend. Older responses
  // (or a rolled-back server) fall through to the single-session wording below.
  const hasCounts = typeof total === 'number' && typeof ok === 'number'

  if (hasCounts) {
    if (total === 0) {
      return { dot: 'bg-slate-300', color: 'text-slate-400', label: 'No machine connected', needsLogin: false }
    }
    if (ok > 0) {
      const age = info.last_success_at ? Date.now() - new Date(info.last_success_at).getTime() : null
      // Stale-but-green is still worth flagging: every machine claims to be
      // signed in, yet nothing has actually come back for hours.
      const stale = age !== null && age >= FRESH_MS
      return {
        dot: stale ? 'bg-amber-500' : 'bg-emerald-500',
        color: stale ? 'text-amber-600' : 'text-emerald-600',
        label: stale
          ? `Working — ${ok} of ${total} signed in (last result ${formatAge(age!)})`
          : `Working — ${ok} of ${total} machine${total === 1 ? '' : 's'} signed in`,
        needsLogin: false,
      }
    }
    // Nobody can query. Whoever is reading this can fix it.
    return info.status === 'expired'
      ? { dot: 'bg-amber-500', color: 'text-red-600', label: `Signed out on all ${total} machine${total === 1 ? '' : 's'}`, needsLogin: true }
      : { dot: 'bg-slate-300', color: 'text-slate-400', label: 'No machine signed in', needsLogin: true }
  }

  // ---- Legacy single-session shape ----
  if (info.status === 'expired') {
    return { dot: 'bg-amber-500', color: 'text-red-600', label: 'Session expired', needsLogin: true }
  }
  if (info.status !== 'ok' || !info.last_success_at) {
    return { dot: 'bg-slate-300', color: 'text-slate-400', label: 'Not logged in yet', needsLogin: true }
  }
  const age = Date.now() - new Date(info.last_success_at).getTime()
  if (age < FRESH_MS) {
    return { dot: 'bg-emerald-500', color: 'text-emerald-600', label: `Active (${formatAge(age)})`, needsLogin: false }
  }
  if (age < STALE_MS) {
    return { dot: 'bg-amber-500', color: 'text-amber-600', label: `Stale (${formatAge(age)})`, needsLogin: true }
  }
  return { dot: 'bg-slate-400', color: 'text-slate-500', label: `Unverified (${formatAge(age)})`, needsLogin: true }
}

/** Turn a backend `probed: false` reason into something a dispatcher can act on. */
function recheckReasonText(reason: string | null, siteLabel: string): string {
  switch (reason) {
    case 'extension_offline':
      return "The extension isn't connected right now, so we can't test the login."
    case 'no_probe_target':
      return 'No container on file to test with — the status will update on the next check.'
    case 'throttled':
      return 'Just checked a moment ago. Give it a few seconds.'
    default:
      return `Couldn't check ${siteLabel} right now.`
  }
}

/**
 * One terminal's session row.
 *
 * Owns the post-Login "did it work?" loop. The signal we lean on is window
 * focus: the user signs in on the terminal's tab and comes back here, and that
 * return is when we ask the backend to run a real query. The 20s timer is a
 * backstop for people who sign in on a second monitor and never switch tabs.
 */
export const SessionRow: React.FC<{
  siteId: string
  info: SessionInfo
  onRecheck: (siteId: string) => Promise<SessionRecheckResult | null>
  /** PCC only: is this tenant's availability link on file? */
  keyConfigured?: boolean
  /** PCC only: rendered under the row so the link can be pasted in place. */
  children?: React.ReactNode
}> = ({ siteId, info, onRecheck, keyConfigured, children }) => {
  const label = SESSION_LABELS[siteId] || siteId
  const display = deriveSessionDisplay(siteId, info, keyConfigured)
  const loginUrl = LOGIN_URLS[siteId]

  const [awaiting, setAwaiting] = useState(false)
  const [checking, setChecking] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const awaitUntilRef = useRef(0)
  // Guards the focus/interval handlers against firing a second query while the
  // first is still in flight — a user alt-tabbing quickly would otherwise
  // hammer the terminal.
  const inFlightRef = useRef(false)

  const runCheck = useCallback(async () => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    setChecking(true)
    setNote(null)
    try {
      const result = await onRecheck(siteId)
      if (!result) {
        setNote("Couldn't reach the server. Try again.")
      } else if (!result.probed) {
        setNote(recheckReasonText(result.reason, label))
      } else if (result.session?.status === 'ok') {
        // Success: the hook already merged the fresh rollup into `sessions`, so
        // the label above has gone green on its own. Drop out of await mode.
        setAwaiting(false)
        setNote(null)
        awaitUntilRef.current = 0
      } else {
        setNote(`Still nobody signed in at ${label}. Finish the login in the other tab, then check again.`)
      }
    } finally {
      inFlightRef.current = false
      setChecking(false)
    }
  }, [onRecheck, siteId, label])

  const startLogin = useCallback(() => {
    const opened = window.open(loginUrl, '_blank', 'noopener,noreferrer')
    if (!opened) {
      // Popup blocker ate it. Saying nothing here reproduces the exact
      // complaint this whole change exists to fix — "I clicked Login and
      // nothing happened" — so name the cause and leave a link they can use.
      setNote('Your browser blocked the new tab. Allow pop-ups for this site, or open the terminal manually.')
      return
    }
    awaitUntilRef.current = Date.now() + AWAIT_LOGIN_WINDOW_MS
    setAwaiting(true)
    setNote(null)
  }, [loginUrl])

  // Re-check when the user comes back to this tab — the natural "I'm done
  // logging in" signal.
  useEffect(() => {
    if (!awaiting) return
    const onFocus = () => {
      if (Date.now() > awaitUntilRef.current) {
        setAwaiting(false)
        return
      }
      void runCheck()
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [awaiting, runCheck])

  // Backstop poll for anyone who signs in without ever refocusing this tab.
  useEffect(() => {
    if (!awaiting) return
    const id = setInterval(() => {
      if (Date.now() > awaitUntilRef.current) {
        setAwaiting(false)
        return
      }
      void runCheck()
    }, AWAIT_LOGIN_POLL_MS)
    return () => clearInterval(id)
  }, [awaiting, runCheck])

  // If the ordinary 30s poll notices the session recovered, stop waiting.
  useEffect(() => {
    if (awaiting && info.status === 'ok') {
      setAwaiting(false)
      setNote(null)
    }
  }, [awaiting, info.status])

  return (
    <div>
      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full ${display.dot} inline-block`} />
        <span className="text-slate-600 w-12">{label}:</span>
        {checking ? (
          <span className="text-slate-500">Checking {label}...</span>
        ) : (
          <span className={display.color}>{display.label}</span>
        )}
        {display.needsLogin && loginUrl && !checking && (
          <button
            type="button"
            onClick={awaiting ? () => void runCheck() : startLogin}
            title={
              awaiting
                ? `Check whether the ${label} sign-in went through`
                : `Open ${label} login in a new tab — come back here and we'll verify it right away`
            }
            className="ml-1 px-1.5 py-0.5 text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded transition-colors cursor-pointer"
          >
            {awaiting ? 'Check now' : 'Login'}
          </button>
        )}
      </div>
      {awaiting && !note && !checking && (
        <div className="ml-[26px] text-[11px] text-slate-400">
          Waiting for sign-in — come back to this tab when you're done.
        </div>
      )}
      {note && <div className="ml-[26px] text-[11px] text-amber-600">{note}</div>}
      {children}
    </div>
  )
}

/**
 * Paste the PCC availability link.
 *
 * Sits under the PCC session row rather than in a settings page because this is
 * where someone finds out it is missing. The link is the tenant's account with
 * PCC — one person pasting it turns exam checking on for the whole company, and
 * it is never shown back, so replacing it means pasting the new one, not
 * editing the old.
 */
const PccKeyRow: React.FC<{
  info?: SiteKeyInfo
  onSave: (url: string) => Promise<string | null>
  onClear: () => Promise<string | null>
}> = ({ info, onSave, onClear }) => {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Removing turns exam checking off for the WHOLE company, and nobody here has
  // the link memorised — getting it back means going to PCC. One confirming
  // click is cheap next to that.
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  const configured = info?.configured === true

  const submit = useCallback(async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    const message = await onSave(value.trim())
    setBusy(false)
    if (message) { setError(message); return }
    setValue('')
    setEditing(false)
  }, [busy, onSave, value])

  if (!editing) {
    return (
      <div className="ml-[26px] flex items-center gap-1.5 text-[11px] text-slate-400">
        {configured ? (
          <>
            <span>
              Link on file
              {info?.updated_by_name ? ` · added by ${info.updated_by_name}` : ''}
              {info?.updated_at ? ` · ${formatDateTime(info.updated_at)}` : ''}
            </span>
            <button
              type="button"
              onClick={() => { setEditing(true); setError(null) }}
              className="text-blue-600 hover:underline cursor-pointer"
            >
              Replace
            </button>
            {confirmingRemove ? (
              <>
                <span className="text-amber-700">Stop checking PCC for everyone?</span>
                <button
                  type="button"
                  onClick={async () => {
                    setBusy(true)
                    const m = await onClear()
                    setBusy(false)
                    setConfirmingRemove(false)
                    if (m) setError(m)
                  }}
                  disabled={busy}
                  className="text-red-600 font-medium hover:underline cursor-pointer disabled:opacity-40"
                >
                  Remove
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingRemove(false)}
                  className="text-slate-400 hover:text-slate-600 hover:underline cursor-pointer"
                >
                  Keep
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => { setConfirmingRemove(true); setError(null) }}
                className="text-slate-400 hover:text-red-600 hover:underline cursor-pointer"
              >
                Remove
              </button>
            )}
          </>
        ) : (
          <button
            type="button"
            onClick={() => { setEditing(true); setError(null) }}
            className="px-1.5 py-0.5 font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded transition-colors cursor-pointer"
          >
            Add PCC link
          </button>
        )}
        {error && <span className="text-amber-600">{error}</span>}
      </div>
    )
  }

  return (
    <div className="ml-[26px] mt-0.5 space-y-0.5">
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
            if (e.key === 'Escape') { setEditing(false); setValue(''); setError(null) }
          }}
          placeholder="Paste the availability link PCC sent you"
          className="flex-1 min-w-0 px-1.5 py-0.5 text-[11px] border border-slate-300 rounded focus:outline-none focus:border-[#00838F]"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy || !value.trim()}
          className="px-1.5 py-0.5 text-[11px] font-medium text-white bg-[#00838F] rounded disabled:opacity-40 cursor-pointer"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => { setEditing(false); setValue(''); setError(null) }}
          className="px-1 text-[11px] text-slate-400 hover:text-slate-600 cursor-pointer"
        >
          Cancel
        </button>
      </div>
      {error
        ? <div className="text-[11px] text-amber-600">{error}</div>
        : <div className="text-[11px] text-slate-400">Whole link, including everything after the “?” — that part is the key.</div>}
    </div>
  )
}

const ExtensionSessionStatus: React.FC<{
  sessions: Record<string, SessionInfo> | null
  siteKeys: Record<string, SiteKeyInfo> | null
  onRecheck: (siteId: string) => Promise<SessionRecheckResult | null>
  onSavePccKey: (url: string) => Promise<string | null>
  onClearPccKey: () => Promise<string | null>
}> = ({ sessions, siteKeys, onRecheck, onSavePccKey, onClearPccKey }) => {
  if (!sessions) return <div className="text-slate-400">Loading...</div>

  return (
    <div className="space-y-0.5 mt-1">
      {Object.entries(sessions).map(([siteId, info]) => {
        // Back-compat: if backend is old and still returns a plain string,
        // coerce it to the SessionInfo shape with no timestamps.
        const normalized: SessionInfo = typeof info === 'string'
          ? { status: info, last_success_at: null, last_failure_at: null, last_failure_reason: null }
          : info
        const isPcc = siteId === 'pcc'
        return (
          <SessionRow
            key={siteId}
            siteId={siteId}
            info={normalized}
            onRecheck={onRecheck}
            keyConfigured={isPcc ? siteKeys?.pcc?.configured === true : undefined}
          >
            {isPcc && (
              <PccKeyRow info={siteKeys?.pcc} onSave={onSavePccKey} onClear={onClearPccKey} />
            )}
          </SessionRow>
        )
      })}
    </div>
  )
}
